import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { Typography } from "@mui/material";
import StatusChip from "../components/common/StatusChip";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import StandardTable, { TableColumn } from "../components/common/StandardTable";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { getInventoryLogs } from "../redux/actions/inventoryLogActions";
import { InventoryLogResponseDto } from "../shared/dtos/inventory/InventoryLogResponseDto";

const InventoryLogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    items: inventoryLogs,
    loading,
    error,
    pagination,
  } = useAppSelector((state) => state.inventoryLog);

  // Local State
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("inventory_log:read_all");

  const fetchLogs = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getInventoryLogs({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch, canRead]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  // --- Table Configuration ---
  const columns: TableColumn<InventoryLogResponseDto>[] = useMemo(
    () => [
      {
        id: "createdAt",
        label: "Date & Time",
        minWidth: 160,
        format: (_, row) => new Date(row.createdAt).toLocaleString(),
      },
      {
        id: "itemName",
        label: "Item",
        minWidth: 150,
        format: (_, row) => (
          <span style={{ fontWeight: 600 }}>{row.itemName}</span>
        ),
      },
      {
        id: "logType",
        label: "Operation",
        minWidth: 140,
        format: (_, row) => <StatusChip status={row.logType} />,
      },
      {
        id: "quantityChange",
        label: "Change",
        minWidth: 100,
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              color: row.quantityChange > 0 ? "success.main" : "error.main",
            }}
          >
            {row.quantityChange > 0
              ? `+${row.quantityChange}`
              : row.quantityChange}
          </Typography>
        ),
      },
      {
        id: "newStockLevel",
        label: "Resulting",
        minWidth: 120,
        format: (_, row) => <b>{row.newStockLevel}</b>,
      },
      { id: "branchName", label: "Branch", minWidth: 120 },
      {
        id: "referenceNumber",
        label: "Ref #",
        minWidth: 120,
        format: (_, row) => row.referenceNumber || "N/A",
      },
      { id: "processedByName", label: "Done By", minWidth: 120 },
      {
        id: "notes",
        label: "Notes",
        minWidth: 150,
        format: (_, row) => (
          <Typography variant="caption" sx={{ fontStyle: "italic" }}>
            {row.notes || "-"}
          </Typography>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Inventory Logs"
      subtitle="Complete history of stock movements, adjustments, and sales across all branches."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by item name, reference, or user..."
      />

      <StandardTable
        columns={columns}
        data={inventoryLogs}
        loading={loading}
        error={error}
        onRetry={fetchLogs}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig({ ...pageConfig, page: newPage })
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig({ ...pageConfig, limit: newLimit, page: 1 })
        }
      />
    </PageContainer>
  );
};

export default InventoryLogsPage;
